import { useQuery } from '@tanstack/react-query';
import { useT } from '@/lib/i18n/useT';
import { usePrefsStore } from '@/lib/prefs/usePrefsStore';
import { atlediApi, type AtlediRecapKpis, type AtlediRecapPayload } from './atlediApi';
import { ATLEDI_ENABLED } from '@/features/ai-assistant/atlediAvailability';

/**
 * PRD §10 — Atledi Recap (3 stacked sections).
 *
 * Phase 4 skeleton. Real narrative + KPI summary come from the Anthropic
 * Sonnet endpoint once it ships (`POST /atledi/recap` with eventId or
 * filters). For now the card renders deterministic placeholders so
 * managers see the layout and product copy locks early.
 *
 * Sections (top to bottom, never tabbed inside the card):
 *   1. Narrative — 2–3 sentence story of what just happened
 *   2. KPI Summary — the three "moments / throughput / inventory" numbers
 *   3. Recommendation — one concrete next action with confidence level
 */
export function AtlediRecapCard({
  narrative,
  kpis,
  recommendation,
  confidence,
  // PRD §16 live wiring: when these are provided, the card calls
  // /atledi/recap and overlays the response on top of the props. The
  // props remain as fallback so storybook + tests can render
  // deterministic copy without hitting the backend.
  liveKpis,
  brandId,
  brandName,
  eventId,
}: {
  narrative?: string;
  kpis?: { label: string; value: string }[];
  recommendation?: string;
  confidence?: 'low' | 'medium' | 'high';
  liveKpis?: AtlediRecapKpis;
  brandId?: number;
  brandName?: string;
  eventId?: number;
}) {
  const t = useT();
  const language = usePrefsStore((s) => s.language);
  const locale: 'en' | 'es' = language === 'es' ? 'es' : 'en';

  // Atledi is opt-in: only fire when the parent passed concrete KPIs to
  // ground the narrative on. Otherwise the card stays a static placeholder.
  const recap = useQuery<AtlediRecapPayload>({
    queryKey: ['atledi-recap', brandId ?? 0, eventId ?? 0, locale, liveKpis],
    enabled: !!liveKpis,
    queryFn: () =>
      atlediApi.recap({
        brandId,
        brandName,
        locale,
        eventId,
        kpis: liveKpis!,
      }),
    staleTime: 4 * 60 * 60 * 1000,
  });

  const live = recap.data;
  const conf = live?.confidence ?? confidence ?? 'medium';
  // Atledi is not released yet. An interactive widget that answers nothing is
  // worse than no widget — see ATLEDI_ENABLED. Placed AFTER every hook: an
  // early return above them changes the hook count between renders the moment
  // the flag flips, which is a crash, not a feature toggle.
  if (!ATLEDI_ENABLED) return null;

  return (
    <section className="rounded-2xl border border-[var(--color-accent-20)] bg-[var(--color-accent-06)] p-5">
      <header className="flex items-center justify-between">
        <p className="text-[10px] font-extrabold uppercase tracking-widest text-[var(--color-accent-ink)]">
          {t('insights.recap.eyebrow')}
        </p>
        <span
          className={`rounded-full px-2 py-0.5 text-[9px] font-bold uppercase tracking-widest ${
            conf === 'high'
              ? 'bg-[var(--color-success)] text-[var(--color-xk-bg-deep)]'
              : conf === 'medium'
                ? 'bg-[var(--color-warn)] text-[var(--color-xk-bg-deep)]'
                : 'bg-[var(--color-error)] text-[var(--color-xk-bg-deep)]'
          }`}
        >
          {t(`insights.recap.confidence.${conf}`)}
        </span>
      </header>

      <div className="mt-3 space-y-4">
        {/* 1 — Narrative */}
        <div>
          <p className="text-[10px] uppercase tracking-widest text-[var(--color-xk-text-muted)]">
            {t('insights.recap.narrative')}
          </p>
          <p className="mt-1 text-sm leading-relaxed text-[var(--color-xk-lavender)]">
            {live?.narrative ?? narrative ?? t('insights.recap.narrativePlaceholder')}
          </p>
        </div>

        {/* 2 — KPI Summary */}
        <div>
          <p className="text-[10px] uppercase tracking-widest text-[var(--color-xk-text-muted)]">
            {t('insights.recap.kpis')}
          </p>
          <div className="mt-2 grid grid-cols-3 gap-2">
            {(live?.kpiSummary ?? kpis ?? [
              { label: t('insights.kpi.moments'), value: '—' },
              { label: t('insights.kpi.throughput'), value: '—' },
              { label: t('insights.kpi.inventory'), value: '—' },
            ]).map((k) => (
              <div
                key={k.label}
                className="rounded-lg border border-[var(--color-xk-border)] bg-[var(--color-xk-bg-deep)] p-3"
              >
                <p className="text-[10px] uppercase tracking-widest text-[var(--color-xk-text-muted)]">
                  {k.label}
                </p>
                <p className="mt-1 text-lg font-bold text-[var(--color-xk-lavender)]">
                  {k.value}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* 3 — Recommendation */}
        <div>
          <p className="text-[10px] uppercase tracking-widest text-[var(--color-xk-text-muted)]">
            {t('insights.recap.recommendation')}
          </p>
          <p className="mt-1 text-sm leading-relaxed text-[var(--color-xk-lavender)]">
            {live?.recommendation ?? recommendation ?? t('insights.recap.recommendationPlaceholder')}
          </p>
        </div>
      </div>
    </section>
  );
}
